import React, { useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { App as AntdApp, Spin } from 'antd';
import { logInfo, logError } from './utils/logger';
import './App.css';

// Components
import PrivateRoute from './components/PrivateRoute';
import RoleRoute from './components/RoleRoute';
import Layout from './components/Layout';

// Pages - static imports (small/auth-related, always needed)
import Login from './pages/Login';
import WeChatCallback from './pages/Login/WeChatCallback';
import WechatSidebar from './pages/WechatSidebar';
import WechatGroupDebug from './pages/WechatGroupDebug';
import WechatSidebarTest from './pages/WechatSidebarTest';
import CaseManagement from './pages/WechatSidebar/CaseManagement';
import WeChatQuickReply from './pages/WechatSidebar/QuickReply';

// WeChat utilities
import { isWeComEnvironment, initWeComSDK, weComAutoLogin } from './utils/wecom';
import { isAuthenticated, getUserInfo } from './utils/auth';
import { startTokenRefresh, stopTokenRefresh, refreshOnActivity } from './utils/tokenRefresh';
import { UserRole } from './constants/user';

// Pages - lazy loaded for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Workspace = lazy(() => import('./pages/Workspace'));
const Lead = lazy(() => import('./pages/Lead'));
const LeadDetail = lazy(() => import('./pages/Lead/Detail'));
const Case = lazy(() => import('./pages/Case'));
const CaseDetail = lazy(() => import('./pages/Case/Detail'));
const Finance = lazy(() => import('./pages/Finance'));
const Config = lazy(() => import('./pages/Config'));
const UserGroup = lazy(() => import('./pages/UserGroup'));
const Users = lazy(() => import('./pages/Config/Users'));
const LeadImport = lazy(() => import('./pages/Settings/LeadImport'));
const OutboundSeats = lazy(() => import('./pages/Config/OutboundSeats'));
const PhoneBlacklist = lazy(() => import('./pages/Config/PhoneBlacklist'));
const EnumManagement = lazy(() => import('./pages/Config/EnumManagement'));
const MonthlyTargets = lazy(() => import('./pages/Config/MonthlyTargets'));
const CommissionConfig = lazy(() => import('./pages/Config/CommissionConfig'));
const CopywritingPage = lazy(() => import('./pages/Resources/Copywriting'));
const MaterialsPage = lazy(() => import('./pages/Resources/Materials'));

// Role-based redirect component
const RoleBasedRedirect: React.FC = () => {
  const userInfo = getUserInfo();
  
  // 根据角色重定向到不同的默认页面
  if (userInfo?.role === UserRole.FINANCE) {
    return <Navigate to="/finance" replace />;
  }
  
  return <Navigate to="/workspace" replace />;
};

const App: React.FC = () => {
  const [wecomInitializing, setWecomInitializing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 启动token自动刷新
  useEffect(() => {
    if (isAuthenticated()) {
      startTokenRefresh();
      const cleanup = refreshOnActivity();
      
      return () => {
        stopTokenRefresh();
        cleanup();
      };
    }
  }, []);

  useEffect(() => {
    const initWecom = async () => {
      // 如果已经登录，跳过自动登录
      if (isAuthenticated()) {
        logInfo('用户已登录，跳过企业微信自动登录');
        return;
      }

      // 如果在登录页面、回调页面或企业微信侧边栏页面，跳过自动登录
      if (
        location.pathname === '/login' || 
        location.pathname.includes('/login/wechat') ||
        location.pathname.startsWith('/wechat/debug')
      ) {
        return;
      }

      // 检查是否在企业微信环境中
      if (isWeComEnvironment()) {
        logInfo('检测到企业微信环境，尝试自动登录');
        setWecomInitializing(true);

        // 保存当前页面路径，登录后返回
        localStorage.setItem('wechat_return_url', location.pathname);

        try {
          // 初始化 JS-SDK
          const sdkReady = await initWeComSDK();

          if (sdkReady) {
            // 尝试自动登录
            const loginSuccess = await weComAutoLogin();

            if (loginSuccess) {
              logInfo('企业微信自动登录成功');
              // 清除保存的路径，因为已经在当前页面
              localStorage.removeItem('wechat_return_url');
            } else {
              logInfo('企业微信自动登录失败，跳转到登录页');
              navigate('/login', { replace: true });
            }
          }
        } catch (error) {
          logError('企业微信初始化失败', error);
        } finally {
          setWecomInitializing(false);
        }
      }
    };

    initWecom();
  }, [navigate, location.pathname]);

  // 显示加载状态
  if (wecomInitializing) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <Spin size="large" />
        <div>正在初始化企业微信...</div>
      </div>
    );
  }

  return (
    <AntdApp>
      <div className="App">
        <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><Spin size="large" /></div>}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/login/wechat/callback" element={<WeChatCallback />} />

          {/* WeChat Sidebar - lightweight page without layout */}
          <Route path="/wechat/sidebar" element={
            <PrivateRoute>
              <WechatSidebar />
            </PrivateRoute>
          } />

          {/* WeChat Sidebar - Case Management Tool */}
          <Route path="/wechat/sidebar/case" element={
            <PrivateRoute>
              <CaseManagement />
            </PrivateRoute>
          } />

          {/* WeChat Sidebar - Quick Reply Tool */}
          <Route path="/wechat/sidebar/quick-reply" element={
            <PrivateRoute>
              <WeChatQuickReply />
            </PrivateRoute>
          } />

          {/* WeChat Group Debug - for troubleshooting */}
          <Route path="/wechat/debug" element={
            <PrivateRoute>
              <WechatGroupDebug />
            </PrivateRoute>
          } />

          {/* WeChat Sidebar Test - for testing sidebar with manual group_id */}
          <Route path="/wechat/sidebar-test" element={
            <PrivateRoute>
              <WechatSidebarTest />
            </PrivateRoute>
          } />

          {/* Protected routes */}
          <Route path="/" element={
            <PrivateRoute>
              <Layout />
            </PrivateRoute>
          }>
            <Route index element={<RoleBasedRedirect />} />
            
            {/* 业务模块 - 员工和管理员可访问 */}
            <Route path="workspace" element={
              <RoleRoute allowedRoles={[UserRole.STAFF, UserRole.LAWYER, UserRole.ADMIN]} redirectTo="/finance">
                <Workspace />
              </RoleRoute>
            } />
            <Route path="leads" element={
              <RoleRoute allowedRoles={[UserRole.STAFF, UserRole.ADMIN]} redirectTo="/cases">
                <Lead />
              </RoleRoute>
            } />
            <Route path="leads/:id" element={
              <RoleRoute allowedRoles={[UserRole.STAFF, UserRole.ADMIN]} redirectTo="/cases">
                <LeadDetail />
              </RoleRoute>
            } />
            <Route path="cases" element={
              <RoleRoute allowedRoles={[UserRole.STAFF, UserRole.LAWYER, UserRole.ADMIN]} redirectTo="/finance">
                <Case />
              </RoleRoute>
            } />
            <Route path="cases/:id" element={
              <RoleRoute allowedRoles={[UserRole.STAFF, UserRole.ADMIN]} redirectTo="/finance">
                <CaseDetail />
              </RoleRoute>
            } />
            
            {/* 财务模块 - 财务和管理员可访问 */}
            <Route path="finance" element={
              <RoleRoute allowedRoles={[UserRole.FINANCE, UserRole.ADMIN]} redirectTo="/workspace">
                <Finance />
              </RoleRoute>
            } />
            
            {/* 数据看板 - 仅管理员可访问 */}
            <Route path="dashboard" element={
              <RoleRoute allowedRoles={[UserRole.ADMIN]} redirectTo="/">
                <Dashboard />
              </RoleRoute>
            } />
            
            {/* 资源库 - 所有用户可访问 */}
            <Route path="resources/copywriting" element={<CopywritingPage />} />
            <Route path="resources/materials" element={<MaterialsPage />} />
            
            {/* 配置中心 - 仅管理员可访问 */}
            <Route path="config" element={
              <RoleRoute allowedRoles={[UserRole.ADMIN]} redirectTo="/">
                <Config />
              </RoleRoute>
            } />
            <Route path="config/user-groups" element={
              <RoleRoute allowedRoles={[UserRole.ADMIN]} redirectTo="/">
                <UserGroup />
              </RoleRoute>
            } />
            <Route path="config/lead-import" element={
              <RoleRoute allowedRoles={[UserRole.ADMIN]} redirectTo="/">
                <LeadImport />
              </RoleRoute>
            } />
            <Route path="config/users" element={
              <RoleRoute allowedRoles={[UserRole.ADMIN]} redirectTo="/">
                <Users />
              </RoleRoute>
            } />
            <Route path="config/outbound-seats" element={
              <RoleRoute allowedRoles={[UserRole.ADMIN]} redirectTo="/">
                <OutboundSeats />
              </RoleRoute>
            } />
            <Route path="config/phone-blacklist" element={
              <RoleRoute allowedRoles={[UserRole.ADMIN]} redirectTo="/">
                <PhoneBlacklist />
              </RoleRoute>
            } />
            <Route path="config/enums" element={
              <RoleRoute allowedRoles={[UserRole.ADMIN]} redirectTo="/">
                <EnumManagement />
              </RoleRoute>
            } />
            <Route path="config/monthly-targets" element={
              <RoleRoute allowedRoles={[UserRole.ADMIN]} redirectTo="/">
                <MonthlyTargets />
              </RoleRoute>
            } />
            <Route path="config/commission" element={
              <RoleRoute allowedRoles={[UserRole.ADMIN]} redirectTo="/">
                <CommissionConfig />
              </RoleRoute>
            } />
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<RoleBasedRedirect />} />
        </Routes>
        </Suspense>
      </div>
    </AntdApp>
  );
};

export default App;